import React, { useState } from 'react';
import { DateTime } from "luxon";

const PrintSurrenderButton = ({ targetId, buttonLabel = "Print Surrender", buttonClass = "print-surrender-button" }) => {
    const [isPreparingPrint, setIsPreparingPrint] = useState(false);

    const handlePrint = () => {
        setIsPreparingPrint(true);
        
        const content = document.getElementById(targetId);
        if (!content) {
            console.error('Print target not found:', targetId);
            setIsPreparingPrint(false);
            return;
        }

        const getValueByLabel = (container, labelText) => {
            if (!container) return '';
            const labels = container.querySelectorAll('label');
            for (let label of labels) {
                if (label.textContent.includes(labelText)) {
                    const input = label.parentElement?.querySelector('input, textarea, select');
                    if (input) return input.value || input.textContent || '';
                    const nextInput = label.nextElementSibling?.querySelector('input, textarea, select');
                    if (nextInput) return nextInput.value || '';
                    const siblingInput = label.nextElementSibling;
                    if (siblingInput && (siblingInput.tagName === 'INPUT' || siblingInput.tagName === 'TEXTAREA' || siblingInput.tagName === 'SELECT')) {
                        return siblingInput.value || siblingInput.textContent || '';
                    }
                }
            }
            return '';
        };

        // Extract all form data directly from the DOM
        const extractAllData = () => {
            const data = {};
            
            // Get staff details
            const staffNameInput = content.querySelector('input[value*="staffName"]') || 
                                   content.querySelector('input[readonly][value]');
            if (staffNameInput) data.staffName = staffNameInput.value;
            
            // Get all text inputs with values
            const allInputs = content.querySelectorAll('input, textarea, select');
            allInputs.forEach(input => {
                const label = input.closest('.form-field')?.querySelector('label')?.textContent?.trim() || 
                             input.previousElementSibling?.textContent?.trim();
                if (input.value && label) {
                    data[label] = input.value;
                }
            });
            
            return data;
        };
        
        const formData = extractAllData();
        
        // Extract Requisition ID
        const extractRequisitionID = () => {
            const reqIdElement = content.querySelector('[class*="requisitionID"], p, strong');
            if (reqIdElement) {
                const match = reqIdElement.textContent?.match(/AM\d+/);
                if (match) return match[0];
            }
            return 'Unknown';
        };
        
        // Extract Staff Details
        const extractStaffDetails = () => {
            const staffSection = content.querySelector('.staff-activity-container .form-group:first-child');
            if (!staffSection) return {};
            
            const staffName = staffSection.querySelector('input[readonly]')?.value || 
                            staffSection.querySelector('.read-only')?.value || 
                            staffSection.querySelector('input')?.value || '';
            
            // Try to get staff level and supervisor from the section
            const allInputs = staffSection.querySelectorAll('input');
            const staffLevel = allInputs[1]?.value || 'Not provided';
            const supervisorName = allInputs[2]?.value || 'Not provided';
            
            return {
                staffName: staffName || 'Not provided',
                staffLevel: staffLevel,
                supervisorName: supervisorName
            };
        };        
        // Extract Activity Details
        const extractActivityDetails = () => {
            const activitySection = content.querySelector('.staff-activity-container .form-group:nth-child(2)');
            if (!activitySection) return {};
            
            const fromDate = activitySection.querySelector('input[name="from"]')?.value || '';
            const toDate = activitySection.querySelector('input[name="to"]')?.value || '';
            const activityName = getValueByLabel(activitySection, 'Activity Name:');
            const activityPurpose = getValueByLabel(activitySection, 'Activity Purpose:');
            const targetClient = getValueByLabel(activitySection, 'Target Client:');
            
            // Direct input queries for these fields
            let numberOfClientsAchieved = '';
            const clientsInput = activitySection.querySelector('input[name="numberOfClientsAchieved"]');
            if (clientsInput) numberOfClientsAchieved = clientsInput.value;
            if (!numberOfClientsAchieved) numberOfClientsAchieved = getValueByLabel(activitySection, 'Number of Clients Achieved:');
            
            const expectedImpact = getValueByLabel(activitySection, 'Expected Impact:');
            
            let achievedImpact = '';
            const achievedImpactInput = activitySection.querySelector('input[name="achievedImpact"]');
            if (achievedImpactInput) achievedImpact = achievedImpactInput.value;
            if (!achievedImpact) achievedImpact = getValueByLabel(activitySection, 'Achieved Impact:');
            
            let country = '';
            const countryInput = activitySection.querySelector('input[name="country"]');
            if (countryInput) country = countryInput.value;
            if (!country) country = getValueByLabel(activitySection, 'Country:');
            
            const hubOffice = getValueByLabel(activitySection, 'Hub Office:');
            
            return {
                from: fromDate,
                to: toDate,
                activityName,
                activityPurpose,
                targetClient,
                numberOfClientsAchieved,
                expectedImpact,
                achievedImpact,
                country,
                hubOffice
            };
        };        
        // Extract Project Details
        const extractProjectDetails = () => {
            const projectSection = content.querySelector('.staff-activity-container .form-group:last-child');
            if (!projectSection) return {};
            
            const projectCode = projectSection.querySelectorAll('.form-field')[0]?.querySelector('input')?.value || '';
            const projectName = projectSection.querySelectorAll('.form-field')[1]?.querySelector('input')?.value || '';
            const budgetLine = projectSection.querySelectorAll('.form-field')[2]?.querySelector('input')?.value || '';
            const payMode = projectSection.querySelectorAll('.form-field')[3]?.querySelector('input')?.value || '';
            const bankName = projectSection.querySelectorAll('.form-field')[4]?.querySelector('input')?.value || '';
            const accountNumber = projectSection.querySelectorAll('.form-field')[5]?.querySelector('input')?.value || '';
            const phoneNumber = projectSection.querySelectorAll('.form-field')[6]?.querySelector('input')?.value || '';
            
            return {
                projectCode,
                projectName,
                budgetLineCode: budgetLine,
                payMode,
                bankName,
                bankAccount: accountNumber,
                phoneNumber
            };
        };
        
        // Extract Cost Driver Actuals
        const extractCostDriverActuals = () => {
            const costDrivers = [];
            const table = content.querySelector('#cost-drivers-wrapper table');
            if (!table) return costDrivers;
            
            const rows = table.querySelectorAll('tbody tr');
            rows.forEach((row, index) => {
                const cells = row.querySelectorAll('td');
                if (cells.length >= 4) {
                    // Get cost driver name
                    let costDriver = '';
                    const select = cells[0]?.querySelector('select');
                    if (select) {
                        costDriver = select.options[select.selectedIndex]?.text || select.value || '';
                    }
                    if (!costDriver) {
                        const input = cells[0]?.querySelector('input');
                        if (input) costDriver = input.value || '';
                    }
                    if (!costDriver) {
                        costDriver = cells[0]?.textContent?.trim() || '';
                    }
                    
                    const quantity = cells[1]?.querySelector('input')?.value || cells[1]?.textContent || '0';
                    const unitPriceRaw = cells[2]?.querySelector('input')?.value || cells[2]?.textContent || '0';
                    const unitPrice = unitPriceRaw.replace(/[^0-9.-]/g, '');
                    const actualSpentRaw = cells[3]?.querySelector('input')?.value || cells[3]?.textContent || '0';
                    const actualSpent = actualSpentRaw.replace(/[^0-9.-]/g, '');
                    
                    if (costDriver && costDriver !== 'Select Cost Driver') {
                        costDrivers.push({
                            costDriver: costDriver,
                            quantity: quantity,
                            unitPrice: unitPrice,
                            actualSpent: actualSpent
                        });
                    }
                }
            });
            
            return costDrivers;
        };
        
        // Extract Advance, Actuals & Variances
        const extractAdvanceVariances = () => {
            const variances = [];
            const table = content.querySelector('#advance-variance-wrapper table');
            if (!table) return variances;
            
            const rows = table.querySelectorAll('tbody tr');
            rows.forEach((row) => {
                const cells = row.querySelectorAll('td');
                if (cells.length >= 5) {
                    const costDriver = cells[0]?.querySelector('input')?.value || cells[0]?.textContent?.trim() || '';
                    const advanceRaw = cells[1]?.querySelector('input')?.value || cells[1]?.textContent || '0';
                    const advance = advanceRaw.replace(/[^0-9.-]/g, '');
                    const actualSpentRaw = cells[2]?.querySelector('input')?.value || cells[2]?.textContent || '0';
                    const actualSpent = actualSpentRaw.replace(/[^0-9.-]/g, '');
                    const varianceRaw = cells[3]?.querySelector('input')?.value || cells[3]?.textContent || '0';
                    const variance = varianceRaw.replace(/[^0-9.-]/g, '');
                    const reasonForVariance = cells[4]?.querySelector('input')?.value || cells[4]?.textContent || '';
                    
                    if (costDriver) {
                        variances.push({
                            costDriver: costDriver,
                            advance: advance,
                            actualSpent: actualSpent,
                            variance: variance,
                            reasonForVariance: reasonForVariance
                        });
                    }
                }
            });
            
            return variances;
        };
        
        // Extract District Pairs
        const extractDistrictPairs = () => {
            const pairs = [];
            
            // Look for all field containers in the activity section
            const activitySection = content.querySelector('.staff-activity-container .form-group:nth-child(2)');
            if (!activitySection) return pairs;
            
            // Find all field containers that contain district and area program
            const containers = activitySection.querySelectorAll('.field-container');
            
            containers.forEach(container => {
                // Get District value
                let district = '';
                const districtSelect = container.querySelector('.custom-select-control');
                if (districtSelect) {
                    const singleValue = districtSelect.querySelector('.css-1dimb5e-singleValue, [class*="singleValue"]');
                    district = singleValue?.textContent || '';
                    if (!district) {
                        const select = districtSelect.querySelector('select');
                        if (select) district = select.options[select.selectedIndex]?.text || '';
                    }
                }
                
                // Get Area Program value - IMPROVED
                let areaProgram = '';
                const textInputs = container.querySelectorAll('input[type="text"]');
                for (let input of textInputs) {
                    const label = input.previousElementSibling;
                    if (label && label.textContent.includes('Area Program')) {
                        areaProgram = input.value || '';
                        break;
                    }
                }
                // If not found by label, try to get any text input that has a value
                if (!areaProgram && textInputs.length > 0) {
                    areaProgram = textInputs[0]?.value || '';
                }
                
                if (district && district !== 'Select District' && district !== '') {
                    pairs.push({
                        district: district.trim(),
                        areaProgram: areaProgram.trim() || 'Not specified'
                    });
                }
            });
            
            // Log for debugging
            console.log('Extracted district pairs:', pairs);
            
            return pairs;
        };

        // Extract signature
        const extractSignature = () => {
            // Look for signature in multiple locations
            let signatureImg = content.querySelector('.signature-container img');
            if (!signatureImg) signatureImg = content.querySelector('.signature-image');
            if (!signatureImg) signatureImg = content.querySelector('.signature-container .signature-image');
            if (!signatureImg) signatureImg = content.querySelector('img[alt*="Signature"]');
            
            if (signatureImg && signatureImg.src) {
                return signatureImg.src;
            }
            return null;
        };        
        // Extract reviewer signatures
        const extractReviewerSignatures = () => {
            const reviewers = [];
            const reviewerItems = content.querySelectorAll('.reviewer-item');
            
            reviewerItems.forEach(item => {
                const img = item.querySelector('img');
                const nameDiv = item.querySelector('div:first-child + div');
                const dateDiv = item.querySelectorAll('div')[2];
                
                reviewers.push({
                    name: nameDiv?.textContent?.trim() || '',
                    date: dateDiv?.textContent?.trim() || '',
                    signatureUrl: img?.src || null
                });
            });
            
            return reviewers;
        };
        
        const requisitionID = extractRequisitionID();
        const staffDetails = extractStaffDetails();
        const activityDetails = extractActivityDetails();
        const projectDetails = extractProjectDetails();
        const costDrivers = extractCostDriverActuals();
        const advanceVariances = extractAdvanceVariances();
        const districtPairs = extractDistrictPairs();
        const signatureImageUrl = extractSignature();
        const reviewerSignatures = extractReviewerSignatures();
        
        // Calculate totals
        const totalActualSpent = costDrivers.reduce((sum, driver) => sum + (parseFloat(driver.actualSpent) || 0), 0);
        const totalAdvance = advanceVariances.reduce((sum, item) => sum + (parseFloat(item.advance) || 0), 0);
        const totalVariance = advanceVariances.reduce((sum, item) => sum + (parseFloat(item.variance) || 0), 0);
        
        const printWindow = window.open('', '_blank', 'width=1200,height=900');
        
        // Show loading screen
        printWindow.document.write(`
            <html>
            <head>
                <title>Preparing Surrender Print...</title>
                <style>
                    body { 
                        font-family: Arial, sans-serif; 
                        display: flex; 
                        justify-content: center; 
                        align-items: center; 
                        height: 100vh; 
                        background: #f5f5f5;
                        margin: 0;
                    }
                    .loading {
                        text-align: center;
                        padding: 30px;
                        background: white;
                        border-radius: 8px;
                        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                    }
                    .spinner {
                        border: 4px solid #f3f3f3;
                        border-top: 4px solid #3498db;
                        border-radius: 50%;
                        width: 50px;
                        height: 50px;
                        animation: spin 1s linear infinite;
                        margin: 0 auto 20px;
                    }
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                </style>
            </head>
            <body>
                <div class="loading">
                    <div class="spinner"></div>
                    <div>Preparing surrender document for printing...</div>
                </div>
            </body>
            </html>
        `);
        printWindow.document.close();
        
        setTimeout(() => {
            printWindow.document.open();
            printWindow.document.write(`
                <html>
                <head>
                    <title>Surrender Form - ${requisitionID}</title>
                    <style>
                        @page {
                            margin: 0;
                            size: A4;
                        }
                        
                        @media print {
                            body {
                                margin: 0;
                                padding: 0;
                            }
                            .signature-image {
                                max-width: 120px;
                                max-height: 60px;
                                print-color-adjust: exact;
                                -webkit-print-color-adjust: exact;
                            }
                            table {
                                page-break-inside: avoid;
                            }
                        }
                        
                        body {
                            font-family: 'Arial', sans-serif;
                            font-size: 12px;
                            line-height: 1.2;
                            color: #000;
                            padding: 5px 20px 10px 20px;
                            margin: 0;
                            background: white;
                        }
                        
                       .print-header {
                            text-align: center;
                            margin-bottom: 10px;
                            padding-top: 0;
                            padding-bottom: 5px;
                            border-bottom: 3px solid #000;
                        }
                        
                        .print-header h1 {
                            font-size: 24px;
                            margin-top: 0;
                            margin-bottom: 5px;
                            color: #2c3e50;
                            text-transform: uppercase;
                        }
                        
                        .info-grid {
                            display: grid;
                            grid-template-columns: repeat(3, 1fr);
                            gap: 10px;
                            margin: 8px 0;
                        }
                        
                        .info-card {
                            background: #f8f9fa;
                            padding: 4px 8px 8px 8px;
                            border-radius: 6px;
                            border: 1px solid #e9ecef;
                        }
                        
                        .info-card h4 {
                            margin-bottom: 8px;
                            margin-top: 0;
                            color: #000;
                            border-bottom: 2px solid #111;
                            padding-bottom: 4px;
                            font-size: 13px;
                            font-weight: bold;
                            text-transform: uppercase;
                        }
                        
                        .field-row {
                            margin-bottom: 4px;
                        }
                        
                        .field-label {
                            font-weight: bold;
                            font-size: 11px;
                            color: #555;
                            text-transform: uppercase;
                            margin-bottom: 2px;
                        }
                        
                        .field-value {
                            padding: 2px 0;
                            word-break: break-word;
                        }
                        
                        .section-title {
                            background: transparent;
                            padding: 0 0 2px 0;
                            margin: 6px 0 4px 0;
                            border-left: none;
                            border-bottom: 1px solid #ccc;
                            font-size: 14px;
                            font-weight: bold;
                            color: #2c3e50;
                        }
                        
                        .data-table {
                            width: 100%;
                            border-collapse: collapse;
                            margin: 6px 0;
                            font-size: 11px;
                        }
                        
                       .data-table th {
                            background: #f0f0f0;
                            color: #000 !important;
                            padding: 4px 8px;
                            text-align: left;
                            font-weight: bold;
                            border: 1px solid #000;
                        }
                        
                        .data-table td {
                            padding: 3px 6px;
                            border: 1px solid #ddd;
                            vertical-align: top;
                        }
                        
                        .data-table tfoot td {
                            background: #e9ecef;
                            font-weight: bold;
                            border-top: 2px solid #333;
                        }
                        
                        .variance-summary {
                            margin-top: 8px;
                            padding: 8px;
                            background: #f8f9fa;
                            border-left: none;
                            border-radius: 4px;
                        }
                        
                        .signature-section {
                            margin-top: 15px;
                            padding-top: 10px;
                            border-top: 1px solid #333;
                            text-align: center;
                        }
                        
                        .signature-label {
                            font-weight: bold;
                            font-size: 11px;
                            color: #555;
                            margin-bottom: 6px;
                        }
                        
                        .signature-grid {
                            display: flex;
                            justify-content: center;
                            flex-wrap: wrap;
                            gap: 40px;
                            margin-top: 8px;
                        }
                        
                        .signature-item {
                            text-align: center;
                            min-width: 150px;
                        }
                        
                        .signature-image {
                            max-width: 150px;
                            max-height: 80px;
                            border-bottom: 1px solid #000;
                            margin-bottom: 8px;
                        }
                        
                        .signature-line {
                            height: 1px;
                            border-bottom: 1px solid #000;
                            width: 150px;
                            margin: 0 auto 8px auto;
                        }
                        
                        .signature-name {
                            font-weight: bold;
                            font-size: 12px;
                        }
                        
                        .signature-date {
                            font-size: 10px;
                            color: #666;
                            margin-top: 4px;
                        }
                        
                        .empty-value {
                            color: #999;
                            font-style: italic;
                        }
                    </style>
                </head>
                <body>
                    <div class="print-header">
                        <h1>SURRENDER FORM</h1>
                    </div>
                    
                    <div class="info-grid">
                        <!-- Staff Details -->
                        <div class="info-card">
                            <h4>STAFF DETAILS</h4>
                            <div class="field-row">
                                <div class="field-label">Staff Name:</div>
                                <div class="field-value">${staffDetails.staffName || 'Not provided'}</div>
                            </div>
                        </div>
                        
                        <!-- Activity Details -->
                        <div class="info-card">
                            <h4>ACTIVITY DETAILS</h4>
                            <div class="field-row">
                                <div class="field-label">Activity Duration:</div>
                                <div class="field-value">From: ${activityDetails.from || 'Not set'} | To: ${activityDetails.to || 'Not set'}</div>
                            </div>
                            <div class="field-row">
                                <div class="field-label">Activity Name:</div>
                                <div class="field-value">${activityDetails.activityName || 'Not provided'}</div>
                            </div>
                            <div class="field-row">
                                <div class="field-label">Activity Purpose:</div>
                                <div class="field-value">${activityDetails.activityPurpose || 'Not provided'}</div>
                            </div>
                            <div class="field-row">
                                <div class="field-label">Target Clients:</div>
                                <div class="field-value">${activityDetails.targetClient || 'Not provided'}</div>
                            </div>
                            <div class="field-row">
                                <div class="field-label">Clients Achieved:</div>
                                <div class="field-value">${activityDetails.numberOfClientsAchieved || '0'}</div>
                            </div>
                            <div class="field-row">
                                <div class="field-label">Expected Impact:</div>
                                <div class="field-value">${activityDetails.expectedImpact || 'Not provided'}</div>
                            </div>
                            <div class="field-row">
                                <div class="field-label">Achieved Impact:</div>
                                <div class="field-value">${activityDetails.achievedImpact || 'Not provided'}</div>
                            </div>
                            <div class="field-row">
                                <div class="field-label">Country:</div>
                                <div class="field-value">${activityDetails.country || 'Not provided'}</div>
                            </div>
                            <div class="field-row">
                                <div class="field-label">Hub Office:</div>
                                <div class="field-value">${activityDetails.hubOffice || 'Not provided'}</div>
                            </div>
                        </div>
                        
                        <!-- Project Details -->
                        <div class="info-card">
                            <h4>PROJECT DETAILS</h4>
                            <div class="field-row">
                                <div class="field-label">Project Code:</div>
                                <div class="field-value">${projectDetails.projectCode || 'Not provided'}</div>
                            </div>
                            <div class="field-row">
                                <div class="field-label">Project Name:</div>
                                <div class="field-value">${projectDetails.projectName || 'Not provided'}</div>
                            </div>
                            <div class="field-row">
                                <div class="field-label">Budget Line:</div>
                                <div class="field-value">${projectDetails.budgetLineCode || 'Not provided'}</div>
                            </div>
                            <div class="field-row">
                                <div class="field-label">Pay Mode:</div>
                                <div class="field-value">${projectDetails.payMode || 'Not provided'}</div>
                            </div>
                            <div class="field-row">
                                <div class="field-label">Bank Name:</div>
                                <div class="field-value">${projectDetails.bankName || 'Not provided'}</div>
                            </div>
                            <div class="field-row">
                                <div class="field-label">Account Number:</div>
                                <div class="field-value">${projectDetails.bankAccount || 'Not provided'}</div>
                            </div>
                            <div class="field-row">
                                <div class="field-label">Phone Number:</div>
                                <div class="field-value">${projectDetails.phoneNumber || 'N/A'}</div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- District and Area Program Pairs -->
                    ${districtPairs.length > 0 ? `
                        <div class="section-title">District & Area Program Coverage</div>
                        <table class="data-table">
                            <thead><tr><th>#</th><th>District</th><th>Area Program</th></tr></thead>
                            <tbody>
                                ${districtPairs.map((pair, idx) => `<tr><td>${idx + 1}</td><td>${pair.district}</td><td>${pair.areaProgram || 'Not specified'}</td></tr>`).join('')}
                            </tbody>
                        </table>
                    ` : '<div class="section-title">District & Area Program Coverage</div><p>No district and area program data available</p>'}                    
                    
                    <!-- Cost Drivers Actuals -->
                    ${costDrivers.length > 0 ? `
                        <div class="section-title">Cost Drivers - Actuals</div>
                        <table class="data-table">
                            <thead><tr><th>#</th><th>Cost Driver</th><th>Quantity</th><th>Unit Price</th><th>Actual Spent</th></tr></thead>
                            <tbody>
                                ${costDrivers.map((driver, idx) => `
                                    <tr>
                                        <td>${idx + 1}</td>
                                        <td>${driver.costDriver}</td>
                                        <td>${driver.quantity}</td>
                                        <td>MK ${parseFloat(driver.unitPrice).toLocaleString()}</td>
                                        <td>MK ${parseFloat(driver.actualSpent).toLocaleString()}</td>
                                    </tr>
                                `).join('')}
                            </tbody>
                            <tfoot>
                                <tr><td colspan="4" style="text-align: right;"><strong>Total Actual Spent:</strong></td><td><strong>MK ${totalActualSpent.toLocaleString()}</strong></td></tr>
                            </tfoot>
                        </table>
                    ` : '<div class="section-title">Cost Drivers - Actuals</div><p>No cost drivers data available</p>'}
                    
                    <!-- Advance, Actuals & Variances -->
                    ${advanceVariances.length > 0 ? `
                        <div class="section-title">Advance, Actuals & Variances</div>
                        <table class="data-table">
                            <thead><tr><th>#</th><th>Cost Driver</th><th>Advance</th><th>Actual Spent</th><th>Variance</th><th>Reason for Variance</th></tr></thead>
                            <tbody>
                                ${advanceVariances.map((item, idx) => `
                                    <tr>
                                        <td>${idx + 1}</td>
                                        <td>${item.costDriver}</td>
                                        <td>MK ${parseFloat(item.advance).toLocaleString()}</td>
                                        <td>MK ${parseFloat(item.actualSpent).toLocaleString()}</td>
                                        <td>MK ${parseFloat(item.variance).toLocaleString()}</td>
                                        <td>${item.reasonForVariance || 'No variance'}</td>
                                    </tr>
                                `).join('')}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colspan="2" style="text-align: right;"><strong>Totals:</strong></td>
                                    <td><strong>MK ${totalAdvance.toLocaleString()}</strong></td>
                                    <td><strong>MK ${totalActualSpent.toLocaleString()}</strong></td>
                                    <td><strong>MK ${totalVariance.toLocaleString()}</strong></td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>                        
                    ` : ''}
                    
                    <!-- Signatures Section -->
                    <div class="signature-section">
                        <div class="signature-grid">
                            <div class="signature-item">
                                <div class="signature-label">Submitted by:</div>
                                ${signatureImageUrl ? 
                                    `<img src="${signatureImageUrl}" alt="Submitter Signature" class="signature-image" />` : 
                                    `<div class=""></div>`
                                }
                                <div class="signature-name">${staffDetails.staffName || 'Submitter'}</div>
                            </div>
                        </div>
                        
                        ${reviewerSignatures.length > 0 ? `
                            <div class="signature-grid" style="margin-top: 20px;">
                                ${reviewerSignatures.map(reviewer => `
                                    <div class="signature-item">
                                        <div class="signature-label"></div>
                                        ${reviewer.signatureUrl ? 
                                            `` : 
                                            ``
                                        }
                                        <div class="signature-name"></div>
                                        <div class="signature-date"></div>
                                    </div>
                                `).join('')}
                            </div>
                        ` : ''}
                    </div>
                </body>
                </html>
            `);
            
            printWindow.document.close();
            
            setTimeout(() => {
                printWindow.focus();
                printWindow.print();
                setTimeout(() => {
                    printWindow.close();
                    setIsPreparingPrint(false);
                }, 1000);
            }, 500);
        }, 1000);
    };

    return (
        <button 
            className={`print-surrender-button ${isPreparingPrint ? 'printing' : ''}`} 
            onClick={handlePrint}
            disabled={isPreparingPrint}
            style={{
                marginRight: '8px',
                padding: '8px 16px',
                cursor: isPreparingPrint ? 'not-allowed' : 'pointer',
                opacity: isPreparingPrint ? 0.7 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '4px'
            }}
        >
            <i className="fas fa-print"></i> 
            {isPreparingPrint ? 'Preparing...' : buttonLabel}
        </button>
    );
};

export default PrintSurrenderButton;